import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { LineItemInput } from "../types";

interface LineItemsEditorProps {
  items: LineItemInput[];
  onChange: (items: LineItemInput[]) => void;
}

function createEmptyItem(): LineItemInput {
  return {
    nombre: "",
    cantidad: 1,
    precioUnitario: 0,
    porcentajeDescuento: 0,
    exento: false,
  };
}

function calculateSubtotal(item: LineItemInput): number {
  const base = item.cantidad * item.precioUnitario;
  const discount = item.porcentajeDescuento
    ? base * (item.porcentajeDescuento / 100)
    : 0;
  return Math.round(base - discount);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(value);
}

export function LineItemsEditor({ items, onChange }: LineItemsEditorProps) {
  const handleItemChange = <K extends keyof LineItemInput>(
    index: number,
    field: K,
    value: LineItemInput[K],
  ) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    onChange(updated);
  };

  const addItem = () => {
    onChange([...items, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Detalle</h3>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium w-8">#</th>
              <th className="px-3 py-2 text-left text-xs font-medium">
                Descripción
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium w-20">
                Cant.
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium w-28">
                Precio
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium w-20">
                Dcto%
              </th>
              <th className="px-3 py-2 text-center text-xs font-medium w-16">
                Exento
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium w-28">
                Subtotal
              </th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((item, index) => (
              <tr key={index} className="hover:bg-muted/30">
                <td className="px-3 py-2 text-sm text-muted-foreground">
                  {index + 1}
                </td>
                <td className="px-3 py-2">
                  <Input
                    value={item.nombre}
                    onChange={(e) =>
                      handleItemChange(index, "nombre", e.target.value)
                    }
                    placeholder="Descripción del item"
                    className="h-8"
                  />
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    value={item.cantidad}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "cantidad",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    min={0}
                    className="h-8 text-right"
                  />
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    value={item.precioUnitario}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "precioUnitario",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    min={0}
                    className="h-8 text-right"
                  />
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    value={item.porcentajeDescuento ?? 0}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "porcentajeDescuento",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    min={0}
                    max={100}
                    className="h-8 text-right"
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={item.exento ?? false}
                    onChange={(e) =>
                      handleItemChange(index, "exento", e.target.checked)
                    }
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </td>
                <td className="px-3 py-2 text-right text-sm font-medium">
                  {formatCurrency(calculateSubtotal(item))}
                </td>
                <td className="px-3 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => removeItem(index)}
                    disabled={items.length <= 1}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addItem}>
        <Plus className="mr-2 h-4 w-4" />
        Agregar línea
      </Button>
    </div>
  );
}
